import React from 'react';
import type { AssignmentSummary } from 'shared/src/wrkthru/types';
import { WrkthruMark } from './wrkthru-mark';

type WrkthruHeaderProps = {
  /** ISO date string (yyyy-MM-dd). */
  date: string;
  assignment: AssignmentSummary;
  operatorName: string;
};

export function WrkthruHeader({ date, assignment, operatorName }: WrkthruHeaderProps) {
  const { dayOfWeek, monthName, day, year } = formatHeaderDate(date);

  return (
    <header className="flex items-end justify-between gap-6 pb-4 border-b border-slate-200">
      <div>
        <div className="flex items-center gap-3">
          <WrkthruMark size={32} />
          <WrkthruWordmark />
          <span className="text-3xl font-medium tracking-tight text-slate-700">
            {operatorName}&apos;s day
          </span>
        </div>
        <p className="mt-1 text-sm text-slate-500">
          <span className="font-semibold text-slate-700">{dayOfWeek}</span> · {monthName} {day},{' '}
          {year}
        </p>
      </div>
      <AssignmentSummaryDisplay assignment={assignment} />
    </header>
  );
}

function WrkthruWordmark() {
  return (
    <span className="text-3xl font-bold tracking-tight text-slate-900">
      Wrkthru
      <span
        style={{
          background: 'linear-gradient(135deg, #b6a258 0%, #bc45d3 55%, #e040fb 100%)',
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          color: 'transparent'
        }}>
        _
      </span>
    </span>
  );
}

const DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

function formatHeaderDate(iso: string) {
  // Parse as a local date so the display matches the user's calendar day.
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return {
    dayOfWeek: DOW[dt.getDay()],
    monthName: MONTHS[dt.getMonth()],
    day: dt.getDate(),
    year: dt.getFullYear()
  };
}

function AssignmentSummaryDisplay({ assignment }: { assignment: AssignmentSummary }) {
  const isTrader = assignment.role === 'media-trader';
  const label = isTrader ? 'line items' : 'campaigns assigned';
  return (
    <div className="text-right">
      <div className="flex items-baseline gap-2 justify-end">
        <span className="text-3xl font-semibold text-slate-900">{assignment.primaryCount}</span>
        <span className="text-sm text-slate-500">{label}</span>
      </div>
      {isTrader && assignment.secondaryCount !== undefined && (
        <p className="mt-0.5 text-xs text-slate-500">
          within {assignment.secondaryCount} campaigns
        </p>
      )}
    </div>
  );
}
